import { ISectionLogger } from "@multiweb/logging";
import { AppContext } from "App/AppContext";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { ErrorHandler } from "UI/Errors";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { CancellableEventArgs, Event } from "../../../../Global/Base/Events";
import { CancelError } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { EMPTY_ITEM_RIGHTS_COLLECTION, IItemRightsCollection, RightType } from "../../../../Global/Base/Rights/RightTypes";
import { EnumUtils } from "../../../../Global/Base/Utils/EnumUtils";
import { EOWValidateException } from "../../../../OW.Remoting/Basic/OWExceptions";
import { BasicDataType } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IOWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObjectList";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { ActionType } from "../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { ComponentRightsExtractor } from "../../Rights/ComponentRightsExtractor";
import { ControlRightTypesGroup, RightGroups } from "../../Rights/RightGroups";
import { Rights } from "../../Rights/Rights";
import {
    FormControlWithLoggerParams,
    IAttributeControl,
    IConfirmIfControl,
    IMethodControl,
} from "../Controls/ControlInterfaces";
import { IFRMParams } from "../Controls/FormParameters";
import { LocalizedStringBuilder } from "../DataBuilders/LocalizedStringBuilder";
import { FormManager } from "../FormManager/IFormManager";
import { MessageButtonType, MessageWindow } from "../MessageWindow";
import { UIManager } from "../UIManager";
import { IWindowable, WindowState } from "../UIManagerInterfaces";
import { ActionSharedMethods } from "./Actions/ActionSharedMethods";
import { ReportAction } from "./Actions/ReportAction";
import { StandardAction } from "./Actions/StandardAction";
import { XGAction } from "./Actions/XGAction";
import { DetailFrame } from "./DetailFrame";
import { Form } from "./Form";
import { IValidationMaster } from "./FormControl";
import { FormResult } from "./Forms/FormResult";
import { FrameSelChanged } from "./Frames";
import { IMasterableFrame, IMFNoChangeCheck } from "./Frames/FrameInterfaces";
import { FrameSharedMethods } from "./Frames/FrameSharedMethods";
import { ISeparateDetailFrame } from "./Frames/IDetailMasterableFrame";
import { FormClosingCancellationReason, FrameRefreshType, IForm } from "./IForm";
import { IFrame } from "./IFrame";
import { ModalWindowType } from "./ModalWindow";
import { SeparateDetailFrameOverrides } from "./SeparateDetailFrameOverrides";
import { ObjectCheckingResult, SharedMethods } from "./SharedMethods";
import { ValidationManager } from "./ValidationManager";

export class SeparateDetailFrame extends DetailFrame implements IValidationMaster, ISeparateDetailFrame, IMasterableFrame, IMFNoChangeCheck, IMethodControl, IConfirmIfControl {
    //#region Fields
    public readonly canBeMasterFrame: boolean = true;
    public readonly canUseLikeSeparateDetailFrame: boolean = true;
    public readonly loadDataForNewObject: boolean = true;
    public reportName: string;
    public methodName: string;
    public formName: string;
    public hideRetryButton: boolean;
    public hideRunButton: boolean;
    public hideCloseButton: boolean;
    public sendActualObject: boolean;
    public afterGuiCreateMethod: string;
    public afterGuiCreateFill: string;
    public confirmOnClose: string;
    public onCloseMethod: string;

    private _newObjectField: string;
    private _showSettings: boolean = false;
    private _resultFormName: string;
    private _ownerFormPurposeType: MenuType;
    private _resultFormType: MenuType;
    private _logger: ISectionLogger;
    private readonly _executerFrame: IFrame;
    public readonly imfNoChangeCheck: boolean = false;
    public confirmLockey: string;
    protected _rightsDefinition: IItemRightsCollection;
    protected override __rightOverrides: SeparateDetailFrameOverrides;

    /**
     * Citac spusteni udalosti pred zavrenim formulare.
     */
    private _closePreviewRun: number = 0;

    public readonly selectionChanged: Event<IMasterableFrame, FrameSelChanged> = new Event<IMasterableFrame, FrameSelChanged>();

    /**
     * Příznak, zda se má zakázat tlačítko Spustit.
     * Používá se při nastaveném hideRetryButton a ResultFormName pro zakázání opětovného spuštění.
     */
    private _disableRetryButton: boolean = false;
    /**
     * Čas posledního spuštění metody
     */
    private _lastMethodRunTime: string = null;
    /**
     * Dočasně uložený čas posledního spuštění metody pro případ jejího selhání. V tom případě se nenastaví do _lastMethodRunTime
     */
    private _lastMethodRunTimeTemp: string = null;

    /**
     * alternatívní lokalizační klíč pro Použít/Zobrazit
     */
    public retryButtonLc: string;
    private _retryButtonAction: XGAction;
    /**
     * alternatívní lokalizační klíč pro Tisknout / Spustit / Otevřít
     */
    public runButtonLc: string;
    private _runButtonAction: XGAction;

    /**
     * Akce tlačítka Zobrazit výsledky
     */
    private _showResultsButtonAction: XGAction;

    protected _confirmIf: boolean = false;
    //#endregion Fields

    //#region Properties
    public override get masterFrameName(): string {
        return ""; // nikdy nemá mastera
    }

    public override set masterFrameName(_v: string) {
        // nikdy nemá mastera
    }

    public override get actualObject(): OWObject {
        return super.actualObject;
    }

    public override set actualObject(obj: OWObject) {
        const actualObjRefreshedKey = "separateDetailFrameActualObjectRefreshed";
        this._events.removeByKey(actualObjRefreshedKey);

        super.actualObject = obj;

        if (this.actualObject)
            this._events.addWithKey(actualObjRefreshedKey, this.actualObject.dataRefreshed, () => this._refreshDisplayedData());
    }

    public get validationManager(): ValidationManager {
        return this._validationManager;
    }

    public get confirmIf(): boolean {
        return this._confirmIf;
    }

    public set confirmIf(value: boolean) {
        this._confirmIf = value;
    }

    public get disableRetryButton(): boolean {
        return this._disableRetryButton;
    }

    protected override get _hasEditAction(): boolean {
        return true;
    }

    //#endregion Properties

    //#region Constructors

    constructor({ logger, ...otherParams }: FormControlWithLoggerParams) {
        super(otherParams);

        this._logger = logger;

        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */

        /**
         * Ideálně by tu měl být IForm, ale tam executerFrame není a potřebujeme ho kvůli kontextům.
         * V současné době předpokládáme, že nic jiného než Form zde nemůže přijít.
         */
        if (otherParams.ownerForm instanceof Form)
            this._executerFrame = otherParams.ownerForm.executerFrame;

        this.element.classList.add("SeparateDetailFrame");

        this.objectName = this.element.getAttribute("data-ObjectName");
        this.reportName = this.element.getAttribute("data-ReportName");
        this._showSettings = this.getBoolDataAttr("ShowSettings", AppContext.instance.configuration.defaultShowSettings);

        this.methodName = this.element.getAttribute("data-MethodName");
        this._newObjectField = this.element.getAttribute("data-NewObjectField");
        this.formName = this.element.getAttribute("data-FormName");
        this.hideRetryButton = this.getBoolDataAttr("hideretrybutton");
        this.hideRunButton = this.getBoolDataAttr("hiderunbutton");
        this.hideCloseButton = DataConverter.toBoolean(this.element.getAttribute("data-hideclosebutton"), false);
        this.confirmOnClose = this.element.getAttribute("data-confirmonclose");
        this.onCloseMethod = this.element.getAttribute("data-onclosemethod");

        this.confirmLockey = this.element.getAttribute("data-confirm");
        if (this.confirmLockey != null)
            this.confirmLockey = "XG-" + this.confirmLockey;
        this._rightsDefinition = ComponentRightsExtractor.loadRightsForTypes(this, RightType.confirmIf);

        this.retryButtonLc = this.element.getAttribute("data-RetryButtonLC");
        this.runButtonLc = this.element.getAttribute("data-RunButtonLC");

        this.afterGuiCreateFill = this.element.getAttribute("data-AfterGUICreateFill");
        this.afterGuiCreateMethod = this.element.getAttribute("data-AfterGUICreateMethod");

        this._resultFormName = this.element.getAttribute("data-resultformname");
        const resultFormType = this.element.getAttribute("data-resultformtype");
        if (resultFormType)
            this._setResultFormType(EnumUtils.getEnumValueFromName(MenuType, resultFormType));

        this.hasSplitter = false;
        this.useMasterContext = false;

        if (this.ownerForm != null) {
            this._events.add(this.ownerForm.readOnlyChanged, (_sender, e) => {
                this.interactive = !e;
                this._evaluateInteractiveRights();
            });
        }

        this._ownerFormPurposeType = this.ownerForm.purposeType;

        this._events.add(this.ownerForm.closingPreview, async (previewArgs, _profileItem, _settedValue) => {
            /**
             * Lze spustit vicekrat a pokazde se zobrazoval novy dialog
             */
            if (this._closePreviewRun === 0) {
                this._closePreviewRun++;
                if (this.confirmOnClose != undefined) {
                    const value = await MessageWindow.showYesNoDialog(new LocalizedStringBuilder(this.confirmOnClose));
                    if (value.result == MessageButtonType.no && !previewArgs.cancelEvent) {
                        previewArgs.cancel();
                        this._closePreviewRun--;
                        return;
                    }

                    if (value.result == MessageButtonType.ok && !previewArgs.cancelEvent) {
                        //this._setOwnerFormActive();
                    }
                }
                if (this.onCloseMethod != undefined) {
                    try {
                        UIManager.instance.showLoader();
                        await this.actualObject.run({
                            methodName: this.onCloseMethod,
                            requestContext: this._getRequestContext(),
                        });
                    } finally {
                        UIManager.instance.hideLoader();
                    }
                }

                this._closePreviewRun--;
            } else {
                //this._setOwnerFormActive();
                previewArgs.cancel();
            }
        });
        this.ownerForm.closingCancelled.add((_sender, args) => {
            this.isFrameClosing = false;
            if (args.cancellationReason === FormClosingCancellationReason.confirmOnCloseAborted) {
                this._newEvaluateRightsGroup(ControlRightTypesGroup.inEdit);
            }
        });
        /*this.ownerForm.closed.add(async (sender, prm) => {
            if (this.onCloseMethod != undefined) {
                await this.actualObject.run(this.onCloseMethod, undefined, undefined);
            }
        });*/
    }

    private _findTopOwnerForm(): IForm {
        let topOwner = this.ownerForm;
        while (topOwner.ownerForm != null && (topOwner.canClose || this.ownerForm === topOwner))
            topOwner = topOwner.ownerForm;

        return topOwner;
    }

    protected override async _initializeDetail(): Promise<void> {
        await super._initializeDetail();

        if (this.hideCloseButton) {
            const topOwner = this._findTopOwnerForm();

            this.ownerForm.canClose = false;

            if (topOwner.canClose || topOwner === this.ownerForm)
                topOwner.setState(WindowState.closeDisabled, this.hideCloseButton);
        }

        if (this.ownerForm.setAttributesOnNewObject != null) {
            if (this.afterGuiCreateFill == undefined)
                this.afterGuiCreateFill = "";
            else if (!this.afterGuiCreateFill.endsWith(";"))
                this.afterGuiCreateFill += ";";

            this.afterGuiCreateFill += this.ownerForm.setAttributesOnNewObject;
        }
    }
    //#endregion Constructors

    //#region Methods

    private _setResultFormType(value: MenuType): void {
        if (value != null) {
            this._resultFormType = value;
        } else {
            this._logger.error("ResultFormType is not defined."); // `
            this._resultFormType = MenuType.mtNone;
        }
    }

    /**
     * Vytvoří ReportAction
     * @param locKey Lokalizační klíč přenášený na komponentu
     * @param closeAfter Zavřít formulář po tisku
     * @param priority Priorita tiskové akce (pořadí zobrazení v menu)
     * @returns Vytvořená akce
     */
    private _createReportActionControl(locKey: string, closeAfter: boolean = false, priority: number = undefined): XGAction {
        const divElem = document.createElement("div");
        divElem.setAttribute("data-reportname", this.reportName);
        divElem.setAttribute("data-ShowSettings", this._showSettings + "");
        divElem.setAttribute("data-lockey", locKey);
        const xgAction: XGAction = new ReportAction({ element: divElem, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        xgAction.owner = this;
        xgAction.toMenu = MenuType.mtFormControl;
        if (priority !== undefined) {
            xgAction.priority = priority;
        }
        xgAction.visible = true;
        const reportAction = xgAction as ReportAction;
        reportAction.validateIf = true;
        reportAction.actionType = closeAfter ? ActionType.reportAndClose : ActionType.report;
        reportAction.closeForm = closeAfter;
        this.menu.addAction(xgAction);
        return xgAction;
    }

    private async _checkConfirmCondition(): Promise<boolean> {
        if (this.confirmLockey != undefined && await this._evaluateConfirmIf()) {
            const value = await MessageWindow.showYesNoDialog(new LocalizedStringBuilder(this.confirmLockey));
            if (value.result == MessageButtonType.no)
                return false;
        }
        return true;
    }

    /**
     * Vypsani validacnich zprav ze sluzby
     * @param messages
     */
    private async _checkObjectValidationResult(): Promise<boolean> {
        const res = await SharedMethods.checkObjectBeforeExecute(this.validationManager, this.retryButtonLc ? this.retryButtonLc : "GUI-51363", "GUI-33412"); // "GUI-33412"=Zrušit, GUI-51363=spustit
        return res == ObjectCheckingResult.clear || res == ObjectCheckingResult.recommendedContinue;
    }

    protected async _validateValidatableFrames(checkRecommended?: boolean, checkPostponed?: boolean): Promise<boolean> {
        const results = [];
        for (const aktFrame of this._validatableFrames) {
            // Pokud má frame stejný validateManager, tak se použije validace bez zobrazení hlášek, protože je následně zobrazí SeparateDetailFrame.
            if (aktFrame instanceof IValidationMaster && aktFrame.validationManager === this.validationManager) {
                results.push(await aktFrame.validate(checkRecommended, checkPostponed));
            } else {
                results.push(aktFrame.validateAndShowMessage(checkRecommended, checkPostponed));
            }
        }
        return (await Promise.all(results)).every(result => result);
    }

    public override async validate(checkRecommended?: boolean, checkPostponed?: boolean): Promise<boolean> {
        await this.waitForPendingActions();
        await this.checkEmptyComponents(this.validateRecommended);

        const frameValidation = await this._validateValidatableFrames(checkRecommended, checkPostponed);

        if (this._validationManager.hasErrors || !frameValidation)
            return false;

        try {
            await OWManager.validateObject(this.actualObject.instanceID);

            return true;
        } catch (error) {
            if (error instanceof EOWValidateException) {
                this._handleSaveErrors([{ data: this.actualObject, errors: error.validationErrors }]);
            } else {
                ErrorHandler.instance.handleError(error);
            }

            return false;
        }
    }

    public override async validateAndShowMessage(): Promise<boolean> {
        const valid = await this.validate();
        const res = await this._checkObjectValidationResult();

        const checkCondition = async (): Promise<boolean> => {
            // U standard actiony dojde k vyhodnocení v execute
            if (this._ownerFormPurposeType === MenuType.mtPrint && !this.methodName) {
                return await this._checkConfirmCondition();
            }
            return true;
        };

        this._tempRemoveValidationErrorsFromObjectListAttributes();
        return res && valid && await checkCondition();
    }

    protected override async _initializeFormUI(): Promise<void> {
        this.menu = this.ownerForm.formUI.createFrameMenu(this);
        this._isMenuOwner = true;

        try {
            await this._initializeDom();
            await this.actualizeData();
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        }

        const purposeType = this.ownerForm.purposeType;
        let action: XGAction;

        if (purposeType != null) {
            switch (purposeType) {
                case MenuType.mtActions:
                    if (this.methodName) {
                        this._createMethodActions();
                    } else {
                        this._logger.error("PurposeType is set to 'mtActions', but MethodName is missing.");
                    }
                    break;
                case MenuType.mtForm:
                    // není potřeba nic dělat
                    break;
                case MenuType.mtConditionForm:
                    if (this.formName) {
                        // Pokračovat
                        if (!this.hideRetryButton) {
                            this.retryButtonLc = this.retryButtonLc ? `XG-${this.retryButtonLc}` : "GUI-626119"; // Pokračovat

                            action = new StandardAction(ActionType.standardOpenConditionForm, async () => {
                                this._openForm(true);
                            }, this.retryButtonLc, undefined, undefined, this);
                            action.toMenu = MenuType.mtFormControl;
                            this.menu.addAction(action);
                            this._retryButtonAction = action;
                        }

                        // Pokračovat v nové záložce
                        if (!this.hideRunButton) {
                            this.runButtonLc = this.runButtonLc ? `XG-${this.runButtonLc}` : "GUI-626124"; // Pokračovat v nové záložce
                            action = new StandardAction(ActionType.standardCloseAndOpenConditionForm, async () => {
                                this._openForm(false);
                            }, this.runButtonLc, undefined, undefined, this);
                            action.toMenu = MenuType.mtFormControl;
                            this.menu.addAction(action);
                            this._runButtonAction = action;
                        }
                    } else {
                        this._logger.error("PurposeType is set to 'mtConditionForm', but FormName is missing.");
                    }
                    break;
                case MenuType.mtPrint:
                    if (!this.reportName) {
                        this._logger.error("PurposeType is set to 'mtPrint', but ReportName is missing.");
                    } else {
                        if (this.methodName) {
                            this._createMethodActions();
                            const methodBtn = this._retryButtonAction ?? this._runButtonAction;
                            this._createReportActions(true, methodBtn ? methodBtn.priority - 1 : undefined);
                        } else {
                            this._createReportActions(false);
                        }
                    }
                    break;

                // jen zavře formulář; může přijít jen z kódu MW (ne z PFA/PFB)
                case MenuType.mtWorkflow:
                {
                    const loc = this.retryButtonLc ? `XG-${this.retryButtonLc}` : "GUI-1"; // OK
                    action = new StandardAction(ActionType.standardCloseAfterWF, async (): Promise<void> => {
                        try {
                            UIManager.instance.showLoader();
                            await this.waitForPendingUpdates();

                            await this.actualObject.waitForUpdateInstance();

                            if (await this.validateAndShowMessage()) {
                                this.closeModalWindow();
                            }
                        } catch (error) {
                            if (error instanceof EOWValidateException) {
                                this._handleSaveErrors([{ data: this.actualObject, errors: error.validationErrors }]);

                                await this._checkObjectValidationResult();
                            } else {
                                ErrorHandler.instance.handleError(error);
                            }

                            this._tempRemoveValidationErrorsFromObjectListAttributes();
                        } finally {
                            UIManager.instance.hideLoader();
                        }
                    }, loc, undefined, undefined, this);
                    action.toMenu = MenuType.mtFormControl;
                    this.menu.addAction(action);
                    action.enabled = true;
                    break;
                }
                default:
                    this._logger.error(`PurposeType '${MenuType[purposeType]}' is not supported.`); // `
                    break;
            }
        }
        await this._createCustomActions();

        this._prepareButtons(this);

        this.ownerForm.openInEdit = true;

        await (this._newEvaluateRightsGroup(ControlRightTypesGroup.IED/*TODO-rites: tady by toho mělo asi být víc? - viz initFormUI na DF; c2: nebo na druhou stranu na DF by jich mělo být míň*/).catch(
            PromiseUtils.cancellationCatch));

        this._registerFormUIControls();

        this.menu.setVisibility(this.canValueLoad);
    }

    protected override async _initializeRights(): Promise<void> {
        await super._initializeRights(this._rightsToEvaluateOnActualObjectChange);
        this._events.add(this.actualObjectChanged, () => this._evaluateRightsOnActualObjectChange);
    }

    protected override _createOrGetRightsOverrides(): SeparateDetailFrameOverrides {
        if (!this.__rightOverrides)
            this.__rightOverrides = new SeparateDetailFrameOverrides(this);

        return this.__rightOverrides;
    }

    private _createMethodActions(): void {
        let action: XGAction;
        // Spustit
        if (!this.hideRetryButton || this._resultFormName) {
            this.retryButtonLc = this.retryButtonLc ? `XG-${this.retryButtonLc}` : "GUI-51363"; // Spustit
            action = new StandardAction(ActionType.standardRunMethod, () => this.execute(), this.retryButtonLc, undefined, undefined, this);
            action.toMenu = MenuType.mtFormControl;
            this.menu.addAction(action);
            this._retryButtonAction = action;
        }

        // Spustit a vrátit se
        if (!this.hideRunButton) {
            this.runButtonLc = this.runButtonLc ? `XG-${this.runButtonLc}` : "GUI-480046"; // Spustit a vrátit se
            action = new StandardAction(ActionType.standardRunMethodAndClose, () => this.executeAndClose(), this.runButtonLc, undefined, undefined, this);
            action.toMenu = MenuType.mtFormControl;
            this.menu.addAction(action);
            this._runButtonAction = action;
        }

        // Zobrazit výsledek
        if (this._resultFormName != null) {
            const loc = "GUI-214082"; // Zobrazit výsledek
            action = new StandardAction(ActionType.standardOpenResultForm, async () => {
                await this._openForm(true, this._resultFormName, this._resultFormType, true);
            }, loc, undefined, undefined, this);
            action.toMenu = MenuType.mtFormControl;
            this.menu.addAction(action);
            action.enabled = false;
            this._showResultsButtonAction = action;
        }
    }

    /**
     * Vytvoří akce pro tisk
     * @param separatePrintButton Zda se má vytvořit extra tlačítko pro tisk (např. kvůli metodě)
     * @param priority Priorita akce
     */
    private _createReportActions(separatePrintButton: boolean, priority: number = undefined): void {
        // Tisknout
        const locKey = "GUI-360117";
        if (!this.hideRetryButton || separatePrintButton) {
            const action = this._createReportActionControl(locKey, false, priority); // Tisknout
            if (!separatePrintButton) {
                this._retryButtonAction = action;
                this.retryButtonLc = locKey;
            }
        }

        // Tisknout a zavřít
        if (!this.hideRunButton && !separatePrintButton) {
            this._runButtonAction = this._createReportActionControl("GUI-602672", true, priority); // Tisknout a zavřít
        }
    }

    private _rightsToEvaluateOnActualObjectChange = ControlRightTypesGroup.inEdit;

    protected async _evaluateRightsOnActualObjectChange(): Promise<void> {
        await this._newEvaluateRightsGroup(this._rightsToEvaluateOnActualObjectChange);
    }

    /**
     * Obsluhuje změnu hodnoty na komponentě
     */
    protected override async _valueChange(sender: IAttributeControl): Promise<void> {
        if (!this.actualObject)
            return; // musí být vybrán řádek

        try {
            await this.createContext();
            const changedObject = await this.actualObject.setValue(sender.attrName, sender.controlValue);

            // Pokud se během čekání na odpověď ze serveru SeparateDetailFrame zavřel, změny už neprovádíme.
            if (this.isDisposed) {
                return;
            }

            // pokud aplikační logika vrátila změnu na předchozí hodnotu, tak se hodnota atributu objektu nezmění (a nevyvolá se attributeChanged),
            // ale přesto je potřeba vrátit hodnotu i v komponentě
            // např: atribut obsahuje A, uživatel vloží do editační komponenty B, apl. logika to změní zpátky na A, apl. objekt se nezměnil, ale je potřeba i do editační komponenty vrátit A
            sender.controlValue = await changedObject.getValue(sender.attrName);

            await this._evaluateRightsOnAttributeChanged();
        } catch (e) {
            if (!(e instanceof CancelError))
                ErrorHandler.instance.handleError(e);
        }
    }

    public async actualizeData(): Promise<OWObject> {
        /* MasterLinkToAttr - nazev atributu, do ktereho se ma vlozit objekt,
         * nad kterym se spustila akce otevreni parametrickeho formulare;
         * vlozeni se provadi pokud je zadan.
         * Cilovym atributem muze byt Object (vazba) nebo ObjectList
         */

        let replacingObjectList: IOWObjectList | null = null;
        if (this.objectName && this.ownerForm && this.ownerForm.masterLinkToAttr) {
            const classInfo = await ModelDescriptionsManager.instance.getClassInfo(this.objectName);
            const attrInfo = await classInfo.getAttributeInfo(this.ownerForm.masterLinkToAttr);
            if (attrInfo.basicType === BasicDataType.OWObjectList) {
                if (this.ownerForm.masterObjectList != null) {
                    replacingObjectList = this.ownerForm.masterObjectList;
                } else {
                    replacingObjectList = new OWObjectList();
                    replacingObjectList.add(this.ownerForm.masterObject);
                }
            }
        }

        //after GUI metody uplatnit pokud vytvarim objekt stejne tridy, jake jsou zobrazeny v ListFrame
        const extendedOperationsParams = {
            ownerForm: this.ownerForm,
            masterFrame: this.masterFrame,
            afterGuiCreateMethod: this.afterGuiCreateMethod,
            afterGuiCreateFill: this.afterGuiCreateFill,
            masterLinkToAttr: this.masterLinkToAttr,
            masterAttr: this.masterAttr,
            isMainFrame: true, /* SDF má isMainFrame vždy na false, při skládání extendedParams je s ním ale potřeba pracovat jako s mainFramem */
            isMasterObjectList: !!replacingObjectList,

        };
        const afterGuiMethods = await FrameSharedMethods.createAfterCreateExtendedOperations(extendedOperationsParams, true);

        const createdObject = await OWManager.newObject(this.objectName, afterGuiMethods, this._getRequestContext());

        if (this.ownerForm.masterLinkToAttr == null)
            this.ownerForm.masterObject = createdObject;

        if (replacingObjectList)
            await createdObject.setValue(this.ownerForm.masterLinkToAttr, replacingObjectList);

        this._setActualObject(createdObject);
        this._events.addWithKey(this._getInstanceListenersKey(this.ownerForm.masterObject), this.ownerForm.masterObject.attributesChanged, this._attributesChangedEventMethod);
        this.isEditing = true;

        return createdObject;
    }

    protected async _evaluateConfirmIf(): Promise<boolean> {
        if (this._rightsDefinition.confirmIf) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._rightsDefinition, this._rightsDefinition.confirmIf, this.actualObject);
            return rightResult.confirm;
        }
        return true;
    }

    /**
     * Metoda spouští vyhodnocení práv při změně atributu.
     * Měla by být debounced, v některých případech se můžou zaráz změnit desítky atributů a nechceme spouštět vyhodnocení práv pro každý.
     */
    protected override async __evaluateRightsOnAttributeChanged(): Promise<void> {
        if (!this.actualObject) {
            return;
        }
        if (!this.actualObject.isClientSideEncrypted && this.actualObject.classInfo.encryptingAllowed) {
            await (this._newEvaluateRights([...RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.attributeChange), RightType.useEncryption, RightType.useDecryption, RightType.useDigitalSign]).catch(
                PromiseUtils.cancellationCatch));
        } else {
            await (this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange).catch(
                PromiseUtils.cancellationCatch));
        }
    }

    public override showError(e: any): void {
        ErrorHandler.instance.handleError(e);
    }

    public async executeMethod(closing?: boolean): Promise<boolean> {
        try {
            UIManager.instance.showLoader();
            await this.waitForPendingUpdates();

            await this.actualObject.waitForUpdateInstance();

            let confirmedCondition = false;
            const validated = await this.validateAndShowMessage();

            if (validated) {
                confirmedCondition = await this._checkConfirmCondition();
            }

            if (validated && confirmedCondition) {
                this.isFrameClosing = closing;
                const userLocKey = this.ownerForm ? this.ownerForm.titleLocKey : undefined;
                const result = await this.actualObject.run({
                    methodName: this.methodName,
                    userLocKey,
                    requestContext: this._getRequestContext(),
                });
                ActionSharedMethods.checkMethodResult(result, userLocKey);
                if (this._lastMethodRunTimeTemp) { // pokud metoda prošla, nastaví se čas spuštění z dočasné proměnné
                    this._lastMethodRunTime = this._lastMethodRunTimeTemp;
                    this._lastMethodRunTimeTemp = null;
                }

                if (!closing || (this.formName && this._newObjectField)) {
                    await this.actualObject.reload();
                }

                // při nastaveném hideRetryButton a resultFormName se po prvním spuštění nastaví příznak pro deaktivaci
                // tlačítka Spustit
                if (this.hideRetryButton && this._resultFormName && this._retryButtonAction) {
                    this._disableRetryButton = true;
                    this._retryButtonAction.enabled = false;
                }

                if (this._showResultsButtonAction) {
                    this._showResultsButtonAction.enabled = true; // povolí se akce/tlačítko Zobrazit výsledek
                }

                FrameSharedMethods.refreshFrameAfterSetActive(this.ownerForm);

                // TODO: predelat na zobrazeni zprav v novem okne (zjistit, jestli je mozne pri spusteni metody dostat zpatky validacni chybu)
                await UIManager.instance.showResultMessages(result.messages);

                if (!closing) {
                    // pokud se formulář nezavírá, tak se může nový (s výsledkem operace) otevřít hned, jinak se o to stará kód až po zavření (kvůli drobečkové navigaci)
                    const formOpenData = await this._createNewFormConfiguration();
                    if (formOpenData)
                        SeparateDetailFrame._openNewForm(formOpenData);
                }

                return true;
            } else {
                return false;
            }
        } catch (error) {
            if (error instanceof EOWValidateException) {
                this._handleSaveErrors([{ data: this.actualObject, errors: error.validationErrors }]);

                await this._checkObjectValidationResult();
            } else {
                ErrorHandler.instance.handleError(error);
            }

            this._tempRemoveValidationErrorsFromObjectListAttributes(); //TODO: podivat se na ten temp jestli je jeste potreba

            return false;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Otevře formulář
     * @param modalMode Zda otevřít formulář modálně
     * @param formName Název formuláře, který se má otevřít. Používá se pro "Zobrazit výsledky"
     * @param purposeType V případě předaného parametru formName se otevíranému formuláři nastaví i předaný purposeType - typ otevření
     * @param skipValidation Jestli se má před otevřením okna validovat formulář
     */
    private async _openForm(modalMode: boolean, formName: string = null, purposeType: MenuType | null = null, skipValidation: boolean = false): Promise<void | boolean> {
        try {
            UIManager.instance.showLoader();
            await this.waitForPendingUpdates();

            await this.actualObject.waitForUpdateInstance();

            if (skipValidation || await this.validateAndShowMessage()) {
                const frmParams: IFRMParams = {
                    masterObject: this.actualObject,
                    ownerForm: modalMode ? this.ownerForm : null,
                    executerFrame: this,
                    formContext: this.ownerForm?.formContext,
                };

                let condition: string;
                let conditionParamsString: string = null;
                let conditionParamsAttrs: string[] = [];
                if (formName) { // pokud otevíráme formulář přes "Zobrazit výsledky"
                    condition = `Last_modif_uzivatel = $username and Last_modif_cas >= ${this._lastMethodRunTime.split(".")[0]}`; // ` split odstraní zlomky sekund "2017-08-22T15:25:55.9640011" -> "2017-08-22T15:25:55"
                    if (purposeType != null)
                        frmParams.purposeType = purposeType;
                } else {
                    await this.actualObject.run({
                        methodName: "CreateCondition",
                        requestContext: this._getRequestContext(),
                    });
                    await this.actualObject.refresh();

                    condition = await this.actualObject.getValue("Condition") as string;
                    conditionParamsString = await this.actualObject.getValue("ConditionParams") as string;
                    if (conditionParamsString) {
                        if (conditionParamsString[conditionParamsString.length - 1] === ";")
                            conditionParamsString = conditionParamsString.substring(0, conditionParamsString.length - 1);
                        conditionParamsAttrs = conditionParamsString.split(";");
                    }
                }
                frmParams.formContext = this.ownerForm.formContext;
                if (condition) {
                    frmParams.condition = condition;

                    if (conditionParamsString && !formName) {
                        frmParams.conditionParamsAttrs = conditionParamsAttrs;
                        frmParams.conditionParamsObject = this.actualObject;
                    }
                }

                const openFormName = formName || this.formName; // pokud není parametr, bere se property
                let result: IWindowable;
                if (modalMode)
                    result = await FormManager.instance.openFormModal({ formName: openFormName, parametricData: frmParams, type: ModalWindowType.parametricForm });
                else
                    result = (await FormManager.instance.openForm(openFormName, frmParams)).formGroup.form;
                return result != null;
            } else {
                return false;
            }
        } catch (e) {
            ErrorHandler.instance.handleError(e);
            this._tempRemoveValidationErrorsFromObjectListAttributes();
            return false;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Docasna metoda, ktera natvrdo odstrani vsechny validacni chyby, ktere se vazou k atributu typu OWObjectList -
     * aby slo nevalidani zaznamy na ObjectListu opakovane zkouset ukladat
     * az se udelaji validace na OLE a sledovani zmen na atributech zaznamu v ObjectListu, tak se toto musi odstranit
     */
    private _tempRemoveValidationErrorsFromObjectListAttributes(): void {
        if (!this.actualObject)
            return;

        for (const attr of this.actualObject.classInfo.attributes)
            if (attr.basicType === BasicDataType.OWObjectList)
                this._validationManager.removeDataErrors(this.actualObject.id, attr.attributeName);
    }

    /**
     * Připraví konfigurační data potřebná pro otevření nového formuláře. Vrací null, pokud se nový formulář nemá otevřít nebo je chybná konfigurace.
     */
    private async _createNewFormConfiguration(): Promise<IFormOpenData> {
        if (!this.formName || !this._newObjectField)
            return null;

        try {
            UIManager.instance.showLoader();

            await OWManager.publishInstance(this.actualObject.instanceID, this._newObjectField);

            const attrvalue = await this.actualObject.getValue(this._newObjectField);
            if (attrvalue == null || attrvalue === "") {
                this._logger.warn(`Attribute 'NewObjectField' ('${this._newObjectField}') on record id ${this.actualObject.id} doesn't contain an object. Opening of new form skipped!`);
                return null;
            }

            const obj = attrvalue as OWObject;
            const formInfo = await FrameSharedMethods.getDefaultFormName(this.formName, this.ownerForm, obj, this.frameContext);

            return {
                ownerForm: this.ownerForm,
                objectId: obj.id,
                formName: formInfo.FormName,
                visibleOnlyDetail: true,
            };
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Otevře nový formulář.
     * Používá se pro otevření formuláře po provedení metody SDF.
     * @param openData Data pro konfiguraci otevíraného formuláře
     * @param modal Zda se má nový formulář otevřít modálně
     */
    private static _openNewForm({ ownerForm, objectId, formName, visibleOnlyDetail }: IFormOpenData, modal: boolean = true): void {
        let formConfig: IFRMParams = {
            condition: "ID = " + objectId,
            selectedID: "",
            openInEdit: true,
            ownerForm: modal ? ownerForm : null,
            frameToRefreshAfterSetFormActive: ownerForm.frameToRefreshAfterSetFormActive,
            frameRefreshType: FrameRefreshType.recordOnChange,
            visibleOnlyDetail: visibleOnlyDetail,
            isImplicit: FrameSharedMethods.isFormNameImplicit(formName),
            formContext: ownerForm.formContext,

        };

        if (ObjectUtils.isIdTemporary(objectId)) { // Pokud se jedná o nový neuložený objekt, předáme instanceID v parametru selectedID
            formConfig = {
                ...formConfig,
                condition: "",
                selectedID: objectId,
            };
        }

        if (modal) {
            FormManager.instance.openFormModal({ formName: formName, parametricData: formConfig, type: ModalWindowType.parametricForm });
        } else {
            FormManager.instance.openForm(formName, formConfig);
        }
    }

    public override async closeModalWindow(): Promise<boolean> {
        /**
         * Při zavírání parametrického formuláře se v některém případě (nastaveny vlastnosti FormName a NewObjectField)
         * otevírá nový formulář. Otevírá se formulář podle FormName (nebo podle IMPLICIT) a zobrazuje objekt ve vazbě dané NewObjectField.
         * Pro otevření nového formuláře potřebuje připravit konfigurační data, která získá ze stávajících dat současného formuláře.
         * Ale tato data musí získat ještě před jeho zavřením, protože při dispose se tato data uvolňují a není je už možné použít.
         * Proto je tato data nutné nachystat těsně před zavřením, k čemuž se využívá událost closePreview.
         */
        let formOpenData: IFormOpenData | null = null;

        this._events.add(this.ownerForm.closingPreview, async (previewArgs: CancellableEventArgs) => {
            if (!previewArgs.cancelEvent)
                formOpenData = await this._createNewFormConfiguration();
        }, { oneTimeOnly: true });

        this._events.add(this.ownerForm.closed, () => {
            if (!formOpenData) // není aktivní nastavení, že by se měl otevírat formulář
                return;
            SeparateDetailFrame._openNewForm(formOpenData, false);
        }, { oneTimeOnly: true, asFirst: true }); // první parametr true způsobí automatické uvolnění listeneru, druhý že bude listener vyvolán jako první
        // Při události closed hlavního IForm se zahazuje i jeho iframe, čímž se zruší provádění dalšího (i synchronního) js a tedy i tohoto listeneru.
        // TODO: při WI 22748 (odstranění iframe) odstranit i poslední parametr true a odstranit ho i z třídy Event

        this.isFrameClosing = (await this.ownerForm.close(this.ownerForm.returnMainInstance ? new FormResult(this.actualObject) : undefined));
        return this.isFrameClosing;
    }

    public override async setDetailToEditMode(isEditing: boolean, alsoMasterListFrame: boolean, onlyChangeEditing = true): Promise<void> {
        await this._setDetailToEditMode(isEditing, alsoMasterListFrame, onlyChangeEditing);
    }

    /**
     * Metoda spouštěná pro tlačítko Spustit (UseButton).
     */
    public async execute(): Promise<void> {
        if (await this._executeAndSaveContext(false))
            await this.createContext();
    }

    /**
     * Metoda spuštěná pro tlačítko Spustit a zavřít (RunButton).
     */
    public async executeAndClose(): Promise<void> {
        if (await this._executeAndSaveContext(true))
            await this.closeModalWindow();
    }

    /**
     * Metoda pro spuštění metody a uložení kontextu.
     * @param closing Zda se bude formulář zavírat nebo ne.
     * @returns Výsledek, jestli metoda proběhla.
     */
    private async _executeAndSaveContext(closing?: boolean): Promise<boolean> {
        this._lastMethodRunTimeTemp = await OWManager.getSysDateTime(this._getRequestContext());
        const result = await this.executeMethod(closing);

        if (result)
            await this.saveContext();

        return result;
    }

    /**
     * Metoda na vytváření kontextu.
     */
    public override async createContext(): Promise<void> {
        if (this._executerFrame && !this._executerFrame.isContextCreatedByAction && this.hasHandedContext)
            return await this._executerFrame.createContext();

        return await super.createContext();
    }

    /**
     * Metoda na ukládání kontextu.
     */
    public override async saveContext(): Promise<boolean | void> {
        if (this._executerFrame && !this._executerFrame.isContextCreatedByAction && this.hasHandedContext)
            return await this._executerFrame.saveContext();

        return await super.saveContext();
    }

    /**
     * Metoda na mazání kontextu.
     * */
    public override async rollbackContext(): Promise<boolean | void> {
        if (this._executerFrame && !this._executerFrame.isContextCreatedByAction && this.hasHandedContext)
            return await this._executerFrame.rollbackContext();

        return await super.rollbackContext();
    }
    //#endregion Methods

    //#region IDisposable
    //destruktor
    public override dispose(): void {
        if (super.isDisposed) {
            return;
        }

        if (this.hideCloseButton) {
            const topOwner = this._findTopOwnerForm();
            if (topOwner.canClose)
                topOwner.setState(WindowState.closeDisabled, false); // UIManager.instance.mainMenu.toggleCloseButtonOnActiveMenu(true);
        }

        // SDF si zakládá sám svůj NP objekt, takže by ho měl i uvolnit
        if (this.actualObject && !this.ownerForm.returnMainInstance)
            this.actualObject.dispose();

        this.rollbackContext();

        super.dispose();
    }

    //#endregion IDisposable
}

interface IFormOpenData {
    ownerForm: IForm;
    objectId: any;
    formName: string;
    visibleOnlyDetail: boolean;
}
